import { DatabaseOutlined, ShopOutlined, TeamOutlined } from '@ant-design/icons'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import AtomSwitch from '@/components/Atoms/AtomSwitch/AtomSwitch'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { getExchangesAccess } from '@/utils/auth/routeAccess'

import cn from './Exchanges.module.scss'
import PartnershipsTab from './tabs/Partnerships/PartnershipsTab'
import { stockImportsApi } from './tabs/StockImports/api/stockImportsApi'
import StockImportsTab from './tabs/StockImports/StockImportsTab/StockImportsTab'
import StoreManagementTab from './tabs/StoreManagement/StoreManagementTab'
import SuppliersTab from './tabs/Suppliers/SuppliersTab'

function Exchanges() {
  const [searchParams] = useSearchParams()
  const tokenRoles = useUserInfoStore((state) => state.tokenRoles)
  const {
    canManagePartnerships,
    canManageStores,
    isStockManager,
    isProductSupplierUser,
  } = getExchangesAccess({ roles: null, tokenRoles })
  const canUseStockImports = isStockManager || isProductSupplierUser
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )

  const menuItems = useMemo(() => {
    const items: Array<{ value: string; label: string; icon: ReactNode }> = []

    if (canManagePartnerships && !isProductSupplierUser) {
      items.push(
        {
          value: 'Партнёрства',
          label: 'Партнёрства',
          icon: <TeamOutlined aria-hidden />,
        },
        {
          value: 'Саплаеры',
          label: 'Саплаеры',
          icon: <ShopOutlined aria-hidden />,
        }
      )
    }

    if (canUseStockImports && !canManagePartnerships) {
      const name = isProductSupplierUser
        ? 'Мои остатки'
        : 'Загрузка остатков поставщиков'
      items.push({
        value: name,
        label: name,
        icon: <DatabaseOutlined aria-hidden />,
      })
    }

    return items
  }, [canManagePartnerships, canUseStockImports, isProductSupplierUser])

  const [currentTab, setCurrentTab] = useState(menuItems[0]?.value ?? '')

  useEffect(() => {
    setCurrentTab((tab) => {
      if (canManageStores && searchParams.get('tab') === 'stores') {
        return 'Управление складами'
      }

      return tab === 'Управление складами' ? (menuItems[0]?.value ?? '') : tab
    })
  }, [canManageStores, menuItems, searchParams])

  useEffect(() => {
    if (currentTab === 'Управление складами' && canManageStores) return
    if (menuItems.some((item) => item.value === currentTab)) return
    setCurrentTab(menuItems[0]?.value ?? '')
  }, [canManageStores, currentTab, menuItems])

  useEffect(() => () => stockImportsApi.clearCaches(), [])

  const isStoresTab = currentTab === 'Управление складами'

  return (
    <section className={cn.exchanges}>
      {!isStoresTab && menuItems.length > 1 && (
        <div className={cn.tabs}>
          <AtomSwitch
            options={menuItems}
            value={currentTab}
            onChange={setCurrentTab}
            ariaLabel="Обмены"
            isOrganisationSelected={isOrganisationSelected}
          />
        </div>
      )}
      {!isStoresTab && menuItems.length === 0 && (
        <div className={cn.emptyState}>
          У вас нет доступа к разделам обменов
        </div>
      )}
      {currentTab === 'Партнёрства' && <PartnershipsTab />}
      {currentTab === 'Саплаеры' && (
        <SuppliersTab
          onPartnershipCreated={() => setCurrentTab('Партнёрства')}
        />
      )}
      {(currentTab === 'Загрузка остатков поставщиков' ||
        currentTab === 'Мои остатки') && <StockImportsTab />}
      {isStoresTab && <StoreManagementTab />}
    </section>
  )
}

export default Exchanges
